import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { TemplateScene } from '@/types/cmsTypes';
import { TemplateScenesService, markTemplateCopyUnclaimed, removeTemplateCopy, type ExistingTemplateCopy } from '@/modules/templateScenes';
import { useAuthUser } from '@/hooks/useAuthUser';
import { useOrganization } from '@/hooks/useOrganization';
import { useServices } from '@/infrastructure/providers/ServiceProvider';

interface Options {
  template: TemplateScene | null;
  open: boolean;
  onClose: () => void;
}

class OpenTemplateError extends Error {}

const fail = (message: string) => new OpenTemplateError(message);

const userMessage = (error: unknown, fallback: string) =>
  error instanceof OpenTemplateError ? error.message : fallback;

export function useOpenTemplateScene({ template, open: modalOpen, onClose }: Options) {
  const { subscriptionPlan, orgInstance, loading: orgLoading, error: orgError, refresh: retryOrg } = useOrganization();
  const { user } = useAuthUser();
  const userId = user?.id;
  const services = useServices();
  const navigate = useNavigate();
  const templateScenesService = useMemo(() => new TemplateScenesService(), []);

  // An attempt for a template no longer shown finishes quietly, without navigating or holding the next one's button
  const activeKey = modalOpen && template ? template.id : null;
  const activeKeyRef = useRef(activeKey);
  useLayoutEffect(() => {
    activeKeyRef.current = activeKey;
  });
  const attemptRef = useRef(0);
  const [loadingKey, setLoadingKey] = useState<string | null>(null);
  const [existing, setExisting] = useState<{ key: string; copy: ExistingTemplateCopy } | null>(null);

  const isLoading = loadingKey !== null && loadingKey === activeKey;
  const existingVersionOpen = !!existing && existing.key === activeKey;

  const canEditDirectly = services.subscriptionPlan.canEditTemplatesDirectly(subscriptionPlan || '');

  const isOwnTemplate =
    !!userId && !!template?.creatorUserId && userId === template.creatorUserId && !canEditDirectly;

  const trackOpened = useCallback(() => {
    if (!template?.originalTemplateSceneInstanceId || !template.creatorUserId) return;
    if (userId === template.creatorUserId) return;
    services.analytics.trackLureoTemplateOpened({
      original_template_scene_instance_id: template.originalTemplateSceneInstanceId,
      template_creator_user_id: template.creatorUserId,
    });
  }, [template, userId, services.analytics]);

  const trackCopied = useCallback(
    (projectInstanceId: string, newSceneInstanceId: string | null) => {
      services.analytics.trackLureoNewSceneCreated({
        project_id: projectInstanceId,
        scene_instance_id: newSceneInstanceId ?? undefined,
        from_template: true,
        template_scene_instance_id: template?.originalTemplateSceneInstanceId,
        template_creator_user_id: template?.creatorUserId,
      });
    },
    [template, services.analytics],
  );

  const beginAttempt = useCallback((key: string) => {
    const attempt = ++attemptRef.current;
    setLoadingKey(key);
    return {
      isCurrent: () => attemptRef.current === attempt && activeKeyRef.current === key,
      end: () => {
        if (attemptRef.current === attempt) setLoadingKey(null);
      },
    };
  }, []);

  const copyTemplate = useCallback(
    async (projectInstanceId: string) => {
      if (!template) throw fail('This template could not be opened. Please try again.');
      try {
        const newSceneInstanceId = await templateScenesService.copyTemplateSceneToProject(template.id, projectInstanceId);
        try {
          await markTemplateCopyUnclaimed(newSceneInstanceId);
        } catch (error) {
          console.error('Error marking the template copy as not yet kept:', error);
        }
        trackOpened();
        trackCopied(projectInstanceId, newSceneInstanceId);
        return newSceneInstanceId;
      } catch (error) {
        console.error('Error copying template:', error);
        throw fail('Could not copy this template. Please try again.');
      }
    },
    [template, templateScenesService, trackOpened, trackCopied],
  );

  const finishCopy = useCallback(
    (newSceneInstanceId: string, isCurrent: () => boolean) => {
      if (!isCurrent()) {
        toast.success(`Your copy of ${template?.title ?? 'the template'} is in My Templates`);
        return;
      }
      toast.success('Template scene copied successfully');
      onClose();
      navigate(`/editor/${newSceneInstanceId}`);
    },
    [template, onClose, navigate],
  );

  const checkAccount = useCallback(() => {
    if (!orgInstance && (orgError || !orgLoading)) {
      toast.error('We could not load your account details.', { action: { label: 'Retry', onClick: () => void retryOrg() } });
      return false;
    }
    if (!orgInstance) {
      toast.error('Your account is still loading. Please try again in a moment.');
      return false;
    }
    return true;
  }, [orgError, orgLoading, orgInstance, retryOrg]);

  const open = useCallback(async () => {
    if (!template || !checkAccount()) return;
    const key = template.id;
    const attempt = beginAttempt(key);

    try {
      if (canEditDirectly) {
        const sceneInstanceId = await templateScenesService.getTemplateSceneInstanceId(template.id);
        if (!sceneInstanceId) throw fail('This template could not be opened. Please try again.');
        if (!attempt.isCurrent()) return;
        trackOpened();
        onClose();
        navigate(`/editor/${sceneInstanceId}`);
        return;
      }

      if (!userId) throw fail('Please sign in to open this template.');

      const lookup = await templateScenesService.findExistingTemplateCopy(userId, template.id);
      if (lookup.kind === 'error') throw fail('Could not check for your existing copy. Please try again.');
      if (lookup.kind === 'found') {
        if (attempt.isCurrent()) setExisting({ key, copy: lookup.copy });
        return;
      }
      if (!attempt.isCurrent()) return;

      const projectInstanceId = await templateScenesService.findOrCreateTemplatesProject();
      if (!projectInstanceId) throw fail('Could not set up My Templates. Please try again.');

      const newSceneInstanceId = await copyTemplate(projectInstanceId);
      finishCopy(newSceneInstanceId, attempt.isCurrent);
    } catch (error) {
      console.error('Error opening template:', error);
      if (attempt.isCurrent()) toast.error(userMessage(error, 'Could not open this template. Please try again.'));
    } finally {
      attempt.end();
    }
  }, [template, checkAccount, beginAttempt, canEditDirectly, templateScenesService, userId, trackOpened, onClose, navigate, copyTemplate, finishCopy]);

  const continueExisting = useCallback(() => {
    if (!existing) return;
    trackOpened();
    onClose();
    navigate(`/editor/${existing.copy.sceneUniqueId}`);
  }, [existing, trackOpened, onClose, navigate]);

  // The new copy is made first; the old one is discarded only after that, and only if it is still a draft copy in My Templates
  const startFresh = useCallback(async () => {
    if (!template || !userId || !existing) return;
    const { key, copy: old } = existing;
    const attempt = beginAttempt(key);

    try {
      const recheck = await templateScenesService.findExistingTemplateCopy(userId, template.id);
      if (recheck.kind === 'error') throw fail('Could not check your existing copy. Please try again.');
      const discardOld = recheck.kind === 'found' && recheck.copy.storyUniqueId === old.storyUniqueId;

      const newSceneInstanceId = await copyTemplate(old.projectUniqueId);

      if (discardOld) {
        try {
          await removeTemplateCopy(services, old.storyUniqueId);
        } catch (error) {
          console.error('Error removing the old template copy:', error);
          toast.error('Your new copy is ready, but the old one could not be removed. You can delete it from My Templates.');
        }
      }

      finishCopy(newSceneInstanceId, attempt.isCurrent);
    } catch (error) {
      console.error('Error replacing existing template copy:', error);
      toast.error(userMessage(error, 'Could not copy this template. Please try again.'));
    } finally {
      attempt.end();
    }
  }, [template, userId, existing, beginAttempt, templateScenesService, copyTemplate, services, finishCopy]);

  const setExistingVersionOpen = useCallback((next: boolean) => {
    if (!next) setExisting(null);
  }, []);

  return {
    isLoading,
    isOwnTemplate,
    canEditDirectly,
    existingVersionOpen,
    existingCopyName: existing?.copy.name ?? null,
    setExistingVersionOpen,
    open,
    continueExisting,
    startFresh,
  };
}
